import {
    PlusOutlined,
    SaveOutlined,
    DeleteOutlined,
    BankOutlined,
    CalendarOutlined,
} from '@ant-design/icons';
import {
    ConfigProvider,
    DatePicker,
    Card,
    Table,
    Space,
    Button,
    Row,
    Col,
    Statistic,
    Modal,
    Form,
    InputNumber,
    Popconfirm,
    Divider,
} from 'antd';
import frFR from 'antd/locale/fr_FR';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Footer from '../../../components/Footer';
import NavBar from '../../../components/NavBar';
import SideBar from '../../../components/SideBar';
import axiosInstance from '../../../variables/api/axiosInstance';
import { displayNumbers } from '../../../variables/numbersFunctions';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

dayjs.locale('fr');

const VersementsVT = () => {
    const [versements, setVersements]         = useState([]);
    const [loading, setLoading]               = useState(false);
    const [selectedMois, setSelectedMois]     = useState(dayjs().startOf('month'));
    const [modalVisible, setModalVisible]     = useState(false);
    const [editingVersement, setEditingVersement] = useState(null);
    const [totaux, setTotaux]                 = useState({ totalVersements: 0, totalTimbre: 0 });
    const [form]                              = Form.useForm();

    useEffect(() => {
        if (selectedMois) chargerVersements();
    }, [selectedMois]);

    const chargerVersements = async () => {
        setLoading(true);
        try {
            const mois = selectedMois.format('YYYY-MM-DD');
            const [versRes, totRes] = await Promise.all([
                axiosInstance.get('/api/vt/versements', { params: { mois } }),
                axiosInstance.get('/api/vt/versements/totaux', { params: { mois } }),
            ]);
            setVersements(versRes.data);
            setTotaux(totRes.data);
        } catch {
            toast.error('Erreur lors du chargement des versements');
        } finally {
            setLoading(false);
        }
    };

    const ouvrirModal = (versement = null) => {
        setEditingVersement(versement);
        if (versement) {
            form.setFieldsValue({
                dateVersement: dayjs(versement.dateVersement),
                montantVerse:  versement.montantVerse,
                reportVeille:  versement.reportVeille,
                soldeAReporter: versement.soldeAReporter,
            });
        } else {
            form.resetFields();
            form.setFieldsValue({ dateVersement: dayjs() });
        }
        setModalVisible(true);
    };

    const sauvegarder = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                ...values,
                dateVersement:  values.dateVersement.format('YYYY-MM-DD'),
                moisReference:  selectedMois.format('YYYY-MM-DD'),
                versementId:    editingVersement?.versementId,
            };
            await axiosInstance.post('/api/vt/versements', payload);
            toast.success('Versement enregistré');
            setModalVisible(false);
            chargerVersements();
        } catch {
            toast.error('Erreur lors de l\'enregistrement');
        }
    };

    const supprimer = async (id) => {
        try {
            await axiosInstance.delete(`/api/vt/versements/${id}`);
            toast.success('Versement supprimé');
            chargerVersements();
        } catch {
            toast.error('Erreur lors de la suppression');
        }
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'dateVersement',
            key: 'date',
            render: (d) => dayjs(d).format('DD/MM/YYYY'),
            sorter: (a, b) => dayjs(a.dateVersement).unix() - dayjs(b.dateVersement).unix(),
        },
        {
            title: 'Report veille',
            dataIndex: 'reportVeille',
            align: 'right',
            render: (v) => displayNumbers(v),
        },
        {
            title: 'Montant versé',
            dataIndex: 'montantVerse',
            align: 'right',
            render: (v) => <strong>{displayNumbers(v)}</strong>,
        },
        {
            title: 'Timbre (100 F)',
            dataIndex: 'timbre',
            align: 'right',
            render: (v) => displayNumbers(v),
        },
        {
            title: 'Solde à reporter',
            dataIndex: 'soldeAReporter',
            align: 'right',
            render: (v) => displayNumbers(v),
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'center',
            render: (_, record) => (
                <Space>
                    <Button size="small" onClick={() => ouvrirModal(record)}>Modifier</Button>
                    <Popconfirm
                        title="Supprimer ce versement ?"
                        onConfirm={() => supprimer(record.versementId)}
                        okText="Oui" cancelText="Non"
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <ConfigProvider locale={frFR}>
            <div className="layout-wrapper layout-content-navbar">
                <div className="layout-container">
                <SideBar currentMenu="op-versements-vt" />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div className="d-sm-flex align-items-center justify-content-between mb-4">
                                <h1 className="h3 mb-0 text-gray-800">
                                    <BankOutlined /> Versements bancaires VT
                                </h1>
                            </div>

                            {/* Sélecteur de mois + bouton ajout */}
                            <Card style={{ marginBottom: 16 }}>
                                <Row gutter={16} align="middle">
                                    <Col>
                                        <CalendarOutlined style={{ marginRight: 8 }} />
                                        <strong>Mois :</strong>
                                    </Col>
                                    <Col>
                                        <DatePicker
                                            picker="month"
                                            value={selectedMois}
                                            format="MMMM YYYY"
                                            onChange={(val) => setSelectedMois(val?.startOf('month'))}
                                            allowClear={false}
                                        />
                                    </Col>
                                    <Col flex="auto" />
                                    <Col>
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={() => ouvrirModal()}
                                        >
                                            Ajouter un versement
                                        </Button>
                                    </Col>
                                </Row>
                            </Card>

                            {/* Statistiques du mois */}
                            <Row gutter={16} style={{ marginBottom: 16 }}>
                                <Col xs={24} md={8}>
                                    <Card>
                                        <Statistic
                                            title="Total versé au mois"
                                            value={totaux.totalVersements}
                                            formatter={(v) => displayNumbers(v)}
                                            suffix="FCFA"
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Card>
                                        <Statistic
                                            title="Total timbre"
                                            value={totaux.totalTimbre}
                                            formatter={(v) => displayNumbers(v)}
                                            suffix="FCFA"
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Card>
                                        <Statistic
                                            title="Nombre de versements"
                                            value={versements.length}
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            {/* Tableau des versements */}
                            <Card title={`Versements — ${selectedMois?.format('MMMM YYYY')}`}>
                                <Table
                                    dataSource={versements}
                                    columns={columns}
                                    rowKey="versementId"
                                    loading={loading}
                                    pagination={false}
                                    size="small"
                                    summary={() => (
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                                            <Table.Summary.Cell index={1} align="right">—</Table.Summary.Cell>
                                            <Table.Summary.Cell index={2} align="right">
                                                <strong>{displayNumbers(totaux.totalVersements)}</strong>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={3} align="right">
                                                <strong>{displayNumbers(totaux.totalTimbre)}</strong>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={4} align="right">—</Table.Summary.Cell>
                                            <Table.Summary.Cell index={5} />
                                        </Table.Summary.Row>
                                    )}
                                />
                            </Card>
                        </div>
                    </div>
                    <Footer />
                </div>
                </div>
            </div>

            {/* Modal saisie / modification */}
            <Modal
                title={editingVersement ? 'Modifier le versement' : 'Nouveau versement'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setModalVisible(false)}>Annuler</Button>,
                    <Button key="save" type="primary" icon={<SaveOutlined />} onClick={sauvegarder}>
                        Enregistrer
                    </Button>,
                ]}
                width={480}
            >
                <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item
                        name="dateVersement"
                        label="Date du versement"
                        rules={[{ required: true, message: 'Date obligatoire' }]}
                    >
                        <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="reportVeille" label="Report de la veille (FCFA)">
                        <InputNumber
                            style={{ width: '100%' }}
                            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                            parser={(v) => v.replace(/\s/g, '')}
                            min={0}
                        />
                    </Form.Item>
                    <Form.Item
                        name="montantVerse"
                        label="Montant versé en banque (FCFA)"
                        rules={[{ required: true, message: 'Montant obligatoire' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                            parser={(v) => v.replace(/\s/g, '')}
                            min={0}
                        />
                    </Form.Item>
                    <Divider dashed style={{ margin: '8px 0' }} />
                    <Form.Item name="soldeAReporter" label="Solde à reporter au lendemain (FCFA)">
                        <InputNumber
                            style={{ width: '100%' }}
                            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                            parser={(v) => v.replace(/\s/g, '')}
                            min={0}
                        />
                    </Form.Item>
                    <div style={{ color: '#888', fontSize: 12 }}>
                        Le timbre (100 FCFA) est calculé automatiquement si le montant versé est supérieur à 0.
                    </div>
                </Form>
            </Modal>
        </ConfigProvider>
    );
};

export default VersementsVT;
